'use client';

import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { X, Plus, Shield, Save, RotateCcw, Pencil, CheckCircle, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { ParsedPolicyState } from './templates/policyTemplates';

// Cedar syntax validation types
interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

interface ValidationError {
  line?: number;
  column?: number;
  message: string;
  type: 'syntax' | 'semantic' | 'reference';
}

interface ValidationWarning {
  message: string;
  type: 'conflict' | 'coverage' | 'best-practice';
}

// Cedar syntax validation function
function validateCedarSyntax(policyText: string): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  if (!policyText.trim()) {
    return { isValid: false, errors: [{ message: 'Policy text is empty', type: 'syntax' }], warnings: [] };
  }

  const lines = policyText.split('\n');

  // Check for valid effect
  const effectMatch = policyText.match(/^(permit|forbid)\s*\(/);
  if (!effectMatch) {
    errors.push({
      line: 1,
      message: 'Policy must start with "permit(" or "forbid("',
      type: 'syntax'
    });
  }

  // Check for balanced parentheses
  let parenCount = 0;
  let braceCount = 0;
  lines.forEach((line, idx) => {
    for (const char of line) {
      if (char === '(') parenCount++;
      if (char === ')') parenCount--;
      if (char === '{') braceCount++;
      if (char === '}') braceCount--;
    }
    // Check for unbalanced at end of policy
    if (idx === lines.length - 1) {
      if (parenCount !== 0) {
        errors.push({
          line: idx + 1,
          message: `Unbalanced parentheses: ${parenCount > 0 ? 'missing closing )' : 'extra closing )'}`,
          type: 'syntax'
        });
      }
      if (braceCount !== 0) {
        errors.push({
          line: idx + 1,
          message: `Unbalanced braces: ${braceCount > 0 ? 'missing closing }' : 'extra closing }'}`,
          type: 'syntax'
        });
      }
    }
  });

  // Check for required elements
  if (!policyText.includes('principal')) {
    errors.push({
      message: 'Policy must include a principal clause',
      type: 'semantic'
    });
  }

  if (!policyText.includes('action')) {
    errors.push({
      message: 'Policy must include an action clause',
      type: 'semantic'
    });
  }

  if (!policyText.includes('resource')) {
    errors.push({
      message: 'Policy must include a resource clause',
      type: 'semantic'
    });
  }

  // Check for semicolon at end
  if (!policyText.trim().endsWith(';')) {
    errors.push({
      message: 'Policy must end with a semicolon',
      type: 'syntax'
    });
  }

  // Check for common syntax issues
  const invalidActionPattern = /action\s*==[^"]*[^"Action:]/;
  if (invalidActionPattern.test(policyText) && policyText.includes('action ==')) {
    const actionLineIdx = lines.findIndex(l => l.includes('action =='));
    if (actionLineIdx >= 0 && !lines[actionLineIdx].includes('Action::')) {
      warnings.push({
        message: 'Action comparisons should use Action::"actionName" format',
        type: 'best-practice'
      });
    }
  }

  // Check for when clause syntax
  if (policyText.includes('when')) {
    const whenMatch = policyText.match(/when\s*\{/);
    if (!whenMatch) {
      errors.push({
        message: '"when" clause must be followed by curly braces: when { ... }',
        type: 'syntax'
      });
    }
  }

  // Check for unless clause syntax
  if (policyText.includes('unless')) {
    const unlessMatch = policyText.match(/unless\s*\{/);
    if (!unlessMatch) {
      errors.push({
        message: '"unless" clause must be followed by curly braces: unless { ... }',
        type: 'syntax'
      });
    }
  }

  // Warnings for best practices
  if (policyText.includes('permit') && !policyText.includes('when') && !policyText.includes('unless')) {
    if (policyText.includes('principal,') && policyText.includes('action,') && policyText.includes('resource')) {
      const hasAnyWildcard =
        !policyText.includes('principal in') &&
        !policyText.includes('principal ==') &&
        !policyText.includes('action ==') &&
        !policyText.includes('resource is') &&
        !policyText.includes('resource ==');

      if (hasAnyWildcard) {
        warnings.push({
          message: 'This policy grants broad access without conditions. Consider adding constraints.',
          type: 'coverage'
        });
      }
    }
  }

  // Check for potential conflicts with forbid policies
  if (policyText.includes('forbid') && policyText.includes('principal,') && policyText.includes('action,')) {
    warnings.push({
      message: 'Forbid policies with broad scope may block legitimate access. Test thoroughly.',
      type: 'conflict'
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

export type PolicyType = 'permit' | 'forbid';

export interface PolicyData {
  id?: string;
  name: string;
  description: string;
  policyText: string;
  priority: number;
}

export interface PolicyBuilderProps {
  // For edit mode
  editMode?: boolean;
  initialPolicy?: PolicyData;
  // Callbacks
  onSave: (data: PolicyData) => void;
  onCancel?: () => void;
  // Configuration
  resources?: string[];
  actions?: string[];
  roles?: string[];
  showTemplates?: boolean;
}

export interface PolicyBuilderRef {
  loadFromTemplate: (state: Partial<ParsedPolicyState>) => void;
  reset: () => void;
}

export const PolicyBuilder = forwardRef<PolicyBuilderRef, PolicyBuilderProps>(function PolicyBuilder({
  editMode = false,
  initialPolicy,
  onSave,
  onCancel,
  resources = ['Matricks::Workflow', 'Matricks::Contact', 'Matricks::Provider', 'Matricks::AgentPipeline', 'Matricks::Field'],
  actions = ['read', 'write', 'delete', 'execute'],
  roles = ['admin', 'developer', 'viewer', 'member', 'manager']
}, ref) {
  // Policy metadata
  const [policyName, setPolicyName] = useState(initialPolicy?.name || '');
  const [description, setDescription] = useState(initialPolicy?.description || '');
  const [priority, setPriority] = useState(initialPolicy?.priority || 10);

  // Policy builder state
  const [effect, setEffect] = useState<PolicyType>('permit');
  const [principal, setPrincipal] = useState<string>('any');
  const [action, setAction] = useState<string>('any');
  const [resource, setResource] = useState<string>('any');
  const [conditions, setConditions] = useState<{ field: string; op: string; value: string }[]>([]);

  // Validation state
  const [isValidating, setIsValidating] = useState(false);

  // Parse initial policy if in edit mode
  useEffect(() => {
    if (editMode && initialPolicy?.policyText) {
      parseExistingPolicy(initialPolicy.policyText);
    }
  }, [editMode, initialPolicy]);

  const parseExistingPolicy = (policyText: string) => {
    // Parse effect
    if (policyText.startsWith('permit')) {
      setEffect('permit');
    } else if (policyText.startsWith('forbid')) {
      setEffect('forbid');
    }

    // Parse action
    const actionMatch = policyText.match(/action\s*==\s*Action::"(\w+)"/);
    if (actionMatch) {
      setAction(actionMatch[1]);
    } else {
      setAction('any');
    }

    // Parse resource
    const resourceMatch = policyText.match(/resource\s+is\s+([\w:]+)/);
    if (resourceMatch) {
      setResource(resourceMatch[1]);
    } else {
      setResource('any');
    }

    // Parse role from context.userRoles.contains
    const roleMatch = policyText.match(/context\.userRoles\.contains\("(\w+)"\)/);
    if (roleMatch) {
      setPrincipal(roleMatch[1]);
    } else {
      setPrincipal('any');
    }

    // Parse conditions from when clause
    const parsedConditions: { field: string; op: string; value: string }[] = [];
    const whenMatch = policyText.match(/when\s*\{([^}]+)\}/s);
    if (whenMatch) {
      const whenClause = whenMatch[1];
      const conditionPatterns = whenClause.split('&&').map(c => c.trim()).filter(c => c);

      conditionPatterns.forEach(cond => {
        if (cond.includes('userRoles.contains')) return; // Skip role conditions

        const condMatch = cond.match(/(\S+)\s*(==|!=|<|>|<=|>=|contains|in)\s*(.+)/);
        if (condMatch) {
          parsedConditions.push({
            field: condMatch[1],
            op: condMatch[2],
            value: condMatch[3].replace(/"/g, '').trim()
          });
        }
      });
    }
    setConditions(parsedConditions);
  };

  const reset = useCallback(() => {
    setPolicyName('');
    setDescription('');
    setPriority(10);
    setEffect('permit');
    setPrincipal('any');
    setAction('any');
    setResource('any');
    setConditions([]);
  }, []);

  const loadFromTemplate = useCallback((state: Partial<ParsedPolicyState>) => {
    if (state.effect) setEffect(state.effect);
    if (state.principal) setPrincipal(state.principal);
    if (state.action) setAction(state.action);
    if (state.resource) setResource(state.resource);
    if (state.conditions) setConditions(state.conditions);
  }, []);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    loadFromTemplate,
    reset
  }), [loadFromTemplate, reset]);

  // Generate Cedar policy from UI state
  const generateCedar = useCallback(() => {
    let policy = `${effect} (\n`;

    // Principal
    policy += `  principal,\n`;

    // Action
    if (action === 'any') {
      policy += `  action,\n`;
    } else {
      policy += `  action == Action::"${action}",\n`;
    }

    // Resource
    if (resource === 'any') {
      policy += `  resource\n`;
    } else {
      policy += `  resource is ${resource}\n`;
    }

    policy += `)`;

    // Conditions (When clause)
    const conditionsList: string[] = [];

    // Role check
    if (principal !== 'any') {
      conditionsList.push(`context.userRoles.contains("${principal}")`);
    }

    // Custom conditions
    conditions.forEach(c => {
      if (c.field && c.value) {
        const prefix = c.field.startsWith('resource.') || c.field.startsWith('context.') || c.field.startsWith('principal.')
          ? ''
          : 'resource.';

        const val = isNaN(Number(c.value)) && c.value !== 'true' && c.value !== 'false' && !c.value.startsWith('"')
          ? `"${c.value}"`
          : c.value;

        conditionsList.push(`${prefix}${c.field} ${c.op} ${val}`);
      }
    });

    if (conditionsList.length > 0) {
      policy += `\nwhen {\n  ${conditionsList.join(' &&\n  ')}\n}`;
    }

    policy += ';';
    return policy;
  }, [effect, principal, action, resource, conditions]);

  // Real-time validation with debounce effect
  const generatedPolicy = generateCedar();

  // Memoized validation result
  const validation = useMemo(() => {
    setIsValidating(true);
    const result = validateCedarSyntax(generatedPolicy);
    // Simulate async validation delay for UI feedback
    setTimeout(() => setIsValidating(false), 100);
    return result;
  }, [generatedPolicy]);

  const handleAddCondition = () => {
    setConditions([...conditions, { field: '', op: '==', value: '' }]);
  };

  const handleRemoveCondition = (idx: number) => {
    setConditions(conditions.filter((_, i) => i !== idx));
  };

  const handleConditionChange = (idx: number, field: 'field' | 'op' | 'value', val: string) => {
    const newConditions = [...conditions];
    newConditions[idx][field] = val;
    setConditions(newConditions);
  };

  const handleSave = () => {
    const data: PolicyData = {
      id: initialPolicy?.id,
      name: policyName,
      description,
      policyText: generateCedar(),
      priority
    };
    onSave(data);
    if (!editMode) {
      reset();
    }
  };

  const isValid = policyName.trim().length > 0 && validation.isValid;

  return (
    <Card className="w-full bg-slate-50/50 border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {editMode ? (
                <Pencil className="h-5 w-5 text-amber-600" />
              ) : (
                <Shield className="h-5 w-5 text-indigo-600" />
              )}
              {editMode ? 'Edit Policy' : 'Create Policy'}
            </CardTitle>
            <CardDescription>
              {editMode
                ? 'Modify the existing policy configuration.'
                : 'Build fine-grained access policies using the visual editor.'}
            </CardDescription>
          </div>
          {!editMode && (
            <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Policy Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-lg border">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Policy Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g., Manager Edit Access"
              value={policyName}
              onChange={(e) => setPolicyName(e.target.value)}
              className={!policyName ? 'border-amber-300' : ''}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Priority <Badge variant="outline" className="ml-2 text-xs">{priority}</Badge>
            </label>
            <div className="pt-2">
              <Slider
                value={[priority]}
                onValueChange={(v) => setPriority(v[0])}
                min={1}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Low (1)</span>
                <span>High (100)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sentence Builder */}
        <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border shadow-sm">
          <Select value={effect} onValueChange={(v: PolicyType) => setEffect(v)}>
            <SelectTrigger className={`w-[100px] font-bold ${effect === 'permit' ? 'text-green-600 border-green-200' : 'text-red-600 border-red-200'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="permit">
                <span className="text-green-600 font-medium">Allow</span>
              </SelectItem>
              <SelectItem value="forbid">
                <span className="text-red-600 font-medium">Deny</span>
              </SelectItem>
            </SelectContent>
          </Select>

          <span className="text-slate-500 font-medium">user with role</span>

          <Select value={principal} onValueChange={setPrincipal}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Any Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Role</SelectItem>
              {roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>

          <span className="text-slate-500 font-medium">to</span>

          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Any Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Perform Any Action</SelectItem>
              {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>

          <span className="text-slate-500 font-medium">on</span>

          <Select value={resource} onValueChange={setResource}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Any Resource" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Resources</SelectItem>
              {resources.map(r => (
                <SelectItem key={r} value={r}>{r.split('::')[1] || r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Conditions Section */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-semibold text-slate-700">Conditions (Optional)</h4>
            <Button variant="outline" size="sm" onClick={handleAddCondition} className="h-8">
              <Plus className="h-3 w-3 mr-1" /> Add Condition
            </Button>
          </div>

          {conditions.length === 0 && (
            <div className="text-sm text-slate-400 italic p-3 border border-dashed rounded text-center bg-slate-50">
              No conditions added. Policy applies unconditionally when role matches.
            </div>
          )}

          {conditions.map((cond, idx) => (
            <div key={idx} className="flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
              <Badge variant="secondary" className="h-7 px-2 font-mono text-xs">
                {idx === 0 ? 'WHERE' : 'AND'}
              </Badge>
              <Input
                placeholder="Attribute (e.g. ownerId)"
                className="w-[180px] h-9 font-mono text-sm"
                value={cond.field}
                onChange={(e) => handleConditionChange(idx, 'field', e.target.value)}
              />
              <Select value={cond.op} onValueChange={(v) => handleConditionChange(idx, 'op', v)}>
                <SelectTrigger className="w-[110px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="==">equals</SelectItem>
                  <SelectItem value="!=">not equals</SelectItem>
                  <SelectItem value="in">is in</SelectItem>
                  <SelectItem value="contains">contains</SelectItem>
                  <SelectItem value="<">less than</SelectItem>
                  <SelectItem value=">">greater than</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Value (e.g. context.userId)"
                className="w-[200px] h-9 font-mono text-sm"
                value={cond.value}
                onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-slate-400 hover:text-red-500"
                onClick={() => handleRemoveCondition(idx)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Live Preview with Validation */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700">Generated Cedar Policy</h4>
            <div className="flex items-center gap-2">
              {isValidating ? (
                <Badge variant="outline" className="bg-slate-100 text-slate-600">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Validating...
                </Badge>
              ) : validation.isValid ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Valid
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  <XCircle className="h-3 w-3 mr-1" />
                  {validation.errors.length} Error{validation.errors.length !== 1 ? 's' : ''}
                </Badge>
              )}
              {validation.warnings.length > 0 && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {validation.warnings.length} Warning{validation.warnings.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>

          <div className="relative">
            <pre className={`p-4 rounded-md font-mono text-sm overflow-x-auto min-h-[100px] ${
              validation.isValid
                ? 'bg-slate-900 text-slate-50'
                : 'bg-red-950 text-red-100 border border-red-800'
            }`}>
              {generatedPolicy}
            </pre>
            <div className="absolute top-2 right-2">
              <Badge variant="outline" className="bg-slate-800 text-slate-300 border-slate-700">
                Cedar
              </Badge>
            </div>
          </div>

          {/* Validation Errors */}
          {validation.errors.length > 0 && (
            <div className="space-y-2">
              {validation.errors.map((error, idx) => (
                <Alert key={idx} variant="destructive" className="py-2">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription className="ml-2 text-sm">
                    {error.line && <span className="font-mono text-xs mr-2">[Line {error.line}]</span>}
                    <span className="font-medium capitalize">{error.type}:</span> {error.message}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          {/* Validation Warnings */}
          {validation.warnings.length > 0 && (
            <div className="space-y-2">
              {validation.warnings.map((warning, idx) => (
                <Alert key={idx} className="py-2 border-yellow-200 bg-yellow-50 text-yellow-800">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="ml-2 text-sm">
                    <span className="font-medium capitalize">{warning.type}:</span> {warning.message}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}
        </div>

        {/* Description & Actions */}
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Description</label>
            <Input
              placeholder="Briefly describe what this policy does..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            {editMode && onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSave} disabled={!isValid}>
              <Save className="h-4 w-4 mr-2" />
              {editMode ? 'Update Policy' : 'Save Policy'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
